import { animate, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { easeOut } from "./variants";

const defaultFormat = (value: number): string => Math.round(value).toString();

interface AnimatedNumberProps {
  readonly value: number;
  /** Render the in-flight value (e.g. currency or percentage formatting). */
  readonly format?: (value: number) => string;
  readonly durationMs?: number;
  readonly className?: string;
}

/**
 * Counts up to `value` the first time it enters the viewport. Honors reduced
 * motion by snapping straight to the final value.
 */
export function AnimatedNumber({
  value,
  format = defaultFormat,
  durationMs = 1000,
  className,
}: AnimatedNumberProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const prefersReducedMotion = useReducedMotion();
  const [display, setDisplay] = useState(() => format(0));

  // Keep the latest formatter in a ref so animation updates always use the
  // current one without making `format` an effect dependency. Inline `format`
  // props (and the default) get a new identity on every render; depending on
  // it would tear down and restart the count-up each frame (onUpdate ->
  // setDisplay -> re-render -> new format -> effect re-run), flickering the
  // number.
  const formatRef = useRef(format);
  formatRef.current = format;

  useEffect(() => {
    if (!inView) {
      return;
    }

    if (prefersReducedMotion) {
      setDisplay(formatRef.current(value));
      return;
    }

    const controls = animate(0, value, {
      duration: durationMs / 1000,
      ease: easeOut,
      onUpdate: (latest) => setDisplay(formatRef.current(latest)),
    });

    return () => controls.stop();
  }, [inView, prefersReducedMotion, value, durationMs]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
